import { NormalizedCustomAttributeDefinition } from 'src/graphql/entities/custom-attributes/custom-attributes.types';
import type { CustomViewEntity } from 'src/graphql/entities/custom-view/custom-view-config/custom-view-config';
import {
  CustomViewAttribute,
  CustomViewFormConditionField,
  UIType,
} from 'src/graphql/entities/custom-view/custom-view.types';
import {
  buildCustomAttributeColumnId,
  buildCustomAttributeJsonPath,
  DISPLAY_VALUE_CAST,
} from './identifiers';
import { CUSTOM_ATTRIBUTE_GRID_BEHAVIOR_BY_TYPE } from './registry';

const DEFAULT_CUSTOM_ATTRIBUTE_GROUP_LABEL = 'Additional Properties';

/**
 * Visible definitions → grid column descriptors (pure metadata). The `::text` projection path is the
 * `accessor`; the data path (Phase 3) projects it and the table (Phase 4) renders via `CustomAttributeCell`.
 */
export function buildCustomAttributeColumnDescriptors(
  defs: NormalizedCustomAttributeDefinition[],
): CustomViewAttribute[] {
  return defs.map((def) => {
    const behavior = CUSTOM_ATTRIBUTE_GRID_BEHAVIOR_BY_TYPE[def.type];

    const descriptor: CustomViewAttribute = {
      group: def.groupLabel ?? DEFAULT_CUSTOM_ATTRIBUTE_GROUP_LABEL,
      attribute: buildCustomAttributeColumnId(def.key),
      attributeLabel: def.label,
      uiType: UIType.Custom,
      accessor: buildCustomAttributeJsonPath(def.key, DISPLAY_VALUE_CAST),
      default: false,
    };

    if (!behavior.sortable) {
      return descriptor;
    }

    if (behavior.compareCast === '::boolean') {
      descriptor.sortingKey = buildCustomAttributeJsonPath(def.key, '::boolean');
      descriptor.sortingAggFunction = 'bool_or';
    } else {
      const sortCast =
        behavior.compareCast === '::timestamptz' ? DISPLAY_VALUE_CAST : behavior.compareCast;

      if (sortCast) {
        descriptor.sortingKey = buildCustomAttributeJsonPath(def.key, sortCast);
      }
    }

    if (descriptor.sortingKey) {
      descriptor.sortingCoalesceDefault = serializeAttributeDefault(def.defaultValue);
    }

    return descriptor;
  });
}

/**
 * Filterable definitions → filter descriptors. Offered only when the type supports filtering AND the
 * backend marks the attribute `filterable` (the per-key index-backed signal from Phase 1).
 */
export function buildCustomAttributeFilterDescriptors(
  defs: NormalizedCustomAttributeDefinition[],
  rootEntity: CustomViewEntity,
): CustomViewFormConditionField[] {
  return defs
    .filter((def) => {
      const behavior = CUSTOM_ATTRIBUTE_GRID_BEHAVIOR_BY_TYPE[def.type];
      return behavior.filterable && behavior.filterUiType !== null;
    })
    .map((def) => {
      const behavior = CUSTOM_ATTRIBUTE_GRID_BEHAVIOR_BY_TYPE[def.type];
      const compareCast = behavior.compareCast ?? DISPLAY_VALUE_CAST;
      const options = buildFilterConditionOptions(def);

      return {
        value: buildCustomAttributeColumnId(def.key),
        accessor: buildCustomAttributeJsonPath(def.key, compareCast),
        label: def.label,
        // non-null: the filter above admits only filterable types
        uiType: behavior.filterUiType!,
        group: def.groupLabel ?? DEFAULT_CUSTOM_ATTRIBUTE_GROUP_LABEL,
        // the list value-editor reads fieldOptions; keep options in sync for any other consumer.
        options,
        fieldOptions: options,
        rootEntity,
        coalesceDefault: serializeAttributeDefault(def.defaultValue),
      };
    });
}

// the definition default (typed unknown) to the wire string the backend interprets per cast. only
// scalar defaults are forwarded; null/undefined and non-scalars (e.g. a multi_select's array
// default) yield undefined so no coalesce is emitted — the array read expression treats an absent
// value as NULL, which is the correct "empty" for overlap and is_empty/is_not_empty.
function serializeAttributeDefault(defaultValue: unknown): string | undefined {
  if (typeof defaultValue === 'boolean') return defaultValue ? 'true' : 'false';

  if (typeof defaultValue === 'number') {
    return Number.isFinite(defaultValue) ? String(defaultValue) : undefined;
  }

  if (typeof defaultValue === 'string') {
    return defaultValue.length > 0 ? defaultValue : undefined;
  }

  return undefined;
}

function buildFilterConditionOptions(
  def: NormalizedCustomAttributeDefinition,
): CustomViewFormConditionField['options'] {
  if (def.type === 'checkbox') {
    return [
      { id: 'true', label: 'Yes', value: 'true' },
      { id: 'false', label: 'No', value: 'false' },
    ];
  }

  if (def.type === 'dropdown' || def.type === 'multi_select') {
    return def.options.map((option) => ({
      id: option.key,
      label: option.value,
      value: option.key,
    }));
  }

  return [];
}
